import { Swiper } from "swiper"
import { Autoplay, Navigation, Pagination } from "swiper/modules"

/**
 * Onereach Media Carousel — replacement for Elementor Pro's media-carousel.
 *
 * Reads its configuration from the widget wrapper's `data-settings`, which Elementor fills from every
 * control declared `frontend_available`. The keys are Elementor Pro's, unchanged, so a migrated
 * instance needs no translation layer.
 */

const DEFAULTS = {
  slides_per_view: '',
  space_between: { size: 10 },
  show_arrows: 'yes',
  pagination: 'bullets',
  autoplay: 'yes',
  autoplay_speed: 5000,
  pause_on_hover: 'yes',
  pause_on_interaction: 'yes',
  loop: 'yes',
  speed: 500,
}

const perView = (value) => {
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) ? 1 : parsed
}

const spaceBetween = (value) => {
  const size = value && typeof value === 'object' ? value.size : value
  const parsed = parseInt(size, 10)
  return Number.isNaN(parsed) ? DEFAULTS.space_between.size : parsed
}

const breakpointsOf = (config) => {
  const responsive = config && config.responsive && config.responsive.breakpoints
  return {
    // Elementor's own values: tablet starts at 768, desktop at 1025.
    tablet: (responsive && responsive.md && responsive.md.value) || 768,
    desktop: (responsive && responsive.lg && responsive.lg.value) || 1025,
  }
}

/**
 * Swiper 10 refuses to loop when there are not enough slides to fill the track twice, and a logo
 * strip is exactly that case: six slides shown six at a time. Cloning the set until it is long
 * enough is what keeps the marquee running. Clones are hidden from assistive tech.
 */
const padSlidesForLoop = (wrapper, minimum) => {
  const originals = Array.from(wrapper.children)

  if (!originals.length) {
    return
  }

  let guard = 0

  while (wrapper.children.length < minimum && guard < 20) {
    originals.forEach((slide) => {
      const clone = slide.cloneNode(true)
      clone.setAttribute('aria-hidden', 'true')
      wrapper.appendChild(clone)
    })
    guard += 1
  }
}

jQuery(window).on('elementor/frontend/init', () => {
  elementorFrontend.hooks.addAction(
    'frontend/element_ready/onereach-media-carousel.default',
    ($element) => {
      const container = $element.find('.onereach-media-carousel').get(0)

      if (!container) {
        return
      }

      const settings = { ...DEFAULTS, ...($element.data('settings') || {}) }
      const breakpoints = breakpointsOf(elementorFrontend.config)
      const isEditMode = elementorFrontend.isEditMode()

      const slidesPerView = {
        mobile: perView(settings.slides_per_view_mobile),
        tablet: perView(settings.slides_per_view_tablet),
        desktop: perView(settings.slides_per_view),
      }
      const space = {
        mobile: spaceBetween(settings.space_between_mobile),
        tablet: spaceBetween(settings.space_between_tablet),
        desktop: spaceBetween(settings.space_between),
      }

      const loop = settings.loop === 'yes'
      const autoplay = settings.autoplay === 'yes' && !isEditMode

      if (loop) {
        const widest = Math.max(slidesPerView.mobile, slidesPerView.tablet, slidesPerView.desktop)
        padSlidesForLoop(container.querySelector('.swiper-wrapper'), widest * 2 + 1)
      }

      const carousel = new Swiper(container, {
        modules: [Autoplay, Navigation, Pagination],
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        loop,
        speed: parseInt(settings.speed, 10) || DEFAULTS.speed,
        // Base config is the mobile one; the breakpoints below are min-width.
        slidesPerView: slidesPerView.mobile,
        spaceBetween: space.mobile,
        autoplay: autoplay && {
          delay: parseInt(settings.autoplay_speed, 10) || DEFAULTS.autoplay_speed,
          disableOnInteraction: settings.pause_on_interaction === 'yes',
          pauseOnMouseEnter: settings.pause_on_hover === 'yes',
        },
        navigation: settings.show_arrows === 'yes' && {
          prevEl: container.querySelector('.swiper-button-prev'),
          nextEl: container.querySelector('.swiper-button-next'),
        },
        pagination: !!settings.pagination && {
          el: container.querySelector('.swiper-pagination'),
          type: settings.pagination,
          clickable: true,
        },
        breakpoints: {
          [breakpoints.tablet]: {
            slidesPerView: slidesPerView.tablet,
            spaceBetween: space.tablet,
          },
          [breakpoints.desktop]: {
            slidesPerView: slidesPerView.desktop,
            spaceBetween: space.desktop,
          },
        },
      })

      // Safari often fires element_ready before the logo images are decoded, so Swiper measures
      // slides with no width and collapses them. Re-measure once each image has actually loaded.
      const recalc = () => carousel.update()

      container.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.naturalWidth) {
          return
        }
        img.addEventListener('load', recalc)
        img.addEventListener('error', recalc)
      })

      jQuery(window).on('load', recalc)
    }
  )
})
